<script>
import FormGroup from '@maison/components/form/FormGroup.vue';
import PhoenixForm from '@maison/components/form/PhoenixForm';
import PhoneField from '@maison/components/form/fields/PhoneField.vue';
import SelectField from '@maison/components/form/fields/SelectField.vue';
import TextField from '@maison/components/form/fields/TextField.vue';
import TextareaField from '@maison/components/form/fields/TextareaField.vue';
import formAddressFieldsMixin from '@phoenix/mixins/formAddressFieldsMixin';
import formBasicMixin from '@phoenix/mixins/formBasicMixin';
import { set } from 'lodash-es';
import { closePanel } from '@phoenix/helpers/panel-helper';

export default {
  components: {
    FormGroup,
    PhoneField,
    SelectField,
    TextField,
    PhoenixForm,
    TextareaField,
  },

  mixins: [formBasicMixin, formAddressFieldsMixin],

  props: {
    reference: {
      type: String,
      required: true,
    },
    internalReference: {
      type: String,
      required: true,
    },
    urlReference: {
      type: String,
      required: true,
    },
    stores: {
      type: Object,
      required: true,
    },
    panelId: {
      type: String,
      default: '',
    },
  },

  computed: {
    formAction() {
      return window.route('api.form.post', { form: 'referenceEnquiry' });
    },
  },

  watch: {
    'fields.country.value'(value) {
      const stores = this.stores[value] ?? [];
      set(this.fields, ['store', 'value'], null);
      set(this.fields, ['store', 'options'], stores);
      set(this.fields, ['store', 'disabled'], value === '' || stores.length === 0);
    },
  },

  created() {
    this.setFields({
      form: {
        name: 'form',
        value: 'referenceEnquiry',
      },
      reference: {
        id: 'reference',
        name: 'reference',
        label: this.$t('Form.reference'),
        value: this.reference,
        required: true,
        type: 'text',
      },
      internalReference: {
        id: 'internalReference',
        name: 'internalReference',
        label: this.$t('Form.internalReference'),
        value: this.internalReference,
        required: true,
        type: 'text',
      },
      urlReference: {
        id: 'urlReference',
        name: 'urlReference',
        label: this.$t('Form.urlReference'),
        value: this.urlReference,
        required: true,
        type: 'text',
      },
      country: {
        id: 'country',
        name: 'country',
        label: this.$t('Form.label.country'),
        value: null,
        options: [],
        required: true,
        type: 'select',
      },
      store: {
        id: 'store',
        name: 'store',
        label: this.$t('Form.label.store'),
        value: null,
        options: [],
        required: false,
        type: 'select',
        disabled: true,
        forceSelect: false,
      },
      message: {
        id: 'message',
        name: 'message',
        label: this.$t('Form.label.message'),
        value: this.message,
        required: false,
        type: 'textarea',
      },
    });
  },

  methods: {
    onSubmit() {
      window.tracking.pushEvent(window.tracking.getProductEnquiry());

      if (this.panelId) {
        closePanel(this.panelId);
      }
    },
  },
};
</script>

<template>
  <phoenix-form
    v-model="fields"
    :redirect-url="redirectUrl"
    :privacy-link="privacyLink"
    :has-form-actions-margin-top="false"
    :should-display-required-text="false"
    :show-errors-in-toast="false"
    :custom-submit-text="$t('Form.submit.sendYourRequest')"
    :action="formAction"
    :on-success-callback="onSubmit"
    override-form-action-classes="form-actions form-actions--stretch col-span-12 mt-md"
    fill-customer-info
    prefill-enabled
    captcha-enabled
    form-type="contact"
    korean-form-type="contact"
  >
    <div class="grid gap-md grid-cols-1 mt-md">
      <form-group :attrs="fields.country" class="form-field">
        <select-field v-model="fields.country.value" />
      </form-group>

      <form-group :attrs="fields.store" class="form-field">
        <select-field v-model="fields.store.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('title')" :attrs="fields.title" class="form-field">
        <select-field v-model="fields.title.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('gender')" :attrs="fields.gender" class="form-field">
        <select-field v-model="fields.gender.value" />
      </form-group>

      <form-group
        v-if="fieldIsPresent('firstName2') && isArabicLanguage"
        :attrs="fields.firstName2"
        class="form-field col-start-1"
      >
        <text-field v-model="fields.firstName2.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('lastName2') && isArabicLanguage" :attrs="fields.lastName2" class="form-field">
        <text-field v-model="fields.lastName2.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('firstName1')" :attrs="fields.firstName1" class="form-field col-start-1">
        <text-field v-model="fields.firstName1.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('lastName1')" :attrs="fields.lastName1" class="form-field">
        <text-field v-model="fields.lastName1.value" />
      </form-group>

      <form-group
        v-if="fieldIsPresent('firstName2') && !isArabicLanguage"
        :attrs="fields.firstName2"
        class="form-field"
      >
        <text-field v-model="fields.firstName2.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('lastName2') && !isArabicLanguage" :attrs="fields.lastName2" class="form-field">
        <text-field v-model="fields.lastName2.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('email')" :attrs="fields.email" class="form-field">
        <text-field v-model="fields.email.value" />
      </form-group>

      <form-group v-if="fieldIsPresent('phone')" :attrs="fields.phone" class="form-field">
        <phone-field v-model="fields.phone.value" />
      </form-group>

      <form-group :attrs="fields.message" class="form-field">
        <textarea-field v-model="fields.message.value" data-cy="form-field-message" />
      </form-group>
    </div>
  </phoenix-form>
</template>
